import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import grey from "@material-ui/core/colors/grey";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import IconButton from "@material-ui/core/IconButton";
import Paper, {PaperProps} from "@material-ui/core/Paper";
import {createStyles, makeStyles, Theme} from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import DeleteIcon from "@material-ui/icons/Delete";
import React, {useCallback, useMemo, useRef, useState, useEffect} from "react";
import Draggable from "react-draggable";

import {ClosableDialogTitle, FluxDialog} from "components/common/components/dialog/FluxDialog";
import {useFluxServices} from "injection/hooks";

import {ElementContext, formatElementContextForPrompt} from "./elementContext";

// Guybrush — internal live CSS editing tool for Flux org members.
//
// Named after Guybrush Threepwood from Monkey Island: with this tool,
// anyone is "a guy with a brush" to paint CSS changes onto the app.
//
// How it works:
// 1. User Ctrl+Alt+Right-clicks an element to open this dialog (see GlobalGuybrush.tsx)
// 2. The clicked element's context (tag, classes, selector chain) is captured and displayed
// 3. User types a natural-language prompt (e.g. "increase font size")
// 4. Prompt + element context are sent to the generateCssV2 Cloud Function, which
//    calls an LLM to produce CSS rules
// 5. The returned CSS is injected into a <style> tag in <head>, applying changes live
// 6. Multiple prompts stack; each can be individually removed
// 7. "Make it so Devin" opens a Devin session pre-loaded with the CSS changes and
//    element context so the changes can be implemented permanently
const CSS_STYLE_ELEMENT_ID = "flux-guybrush";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        dialogPaper: {
            minWidth: 520,
            maxWidth: 680,
            cursor: "move",
        },
        promptInput: {
            marginBottom: theme.spacing(2),
        },
        cssPreview: {
            fontFamily: "monospace",
            fontSize: 12,
            backgroundColor: grey[900],
            color: grey[100],
            padding: theme.spacing(1.5),
            borderRadius: theme.shape.borderRadius,
            maxHeight: 240,
            overflow: "auto",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            marginBottom: theme.spacing(2),
        },
        historyItem: {
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            marginBottom: theme.spacing(1),
            padding: theme.spacing(1),
            borderRadius: theme.shape.borderRadius,
            backgroundColor: theme.palette.background.default,
        },
        historyPrompt: {
            fontSize: 12,
            color: theme.palette.text.secondary,
            marginBottom: theme.spacing(0.5),
        },
        historyCss: {
            fontFamily: "monospace",
            fontSize: 11,
            color: theme.palette.text.primary,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
        },
        devinButton: {
            textTransform: "none",
        },
        copyButton: {
            textTransform: "none",
        },
        copySuccess: {
            color: "#4caf50",
            fontSize: 12,
            marginRight: 8,
        },
        elementContextBox: {
            fontFamily: "monospace",
            fontSize: 11,
            backgroundColor: grey[900],
            color: grey[300],
            padding: theme.spacing(1),
            borderRadius: theme.shape.borderRadius,
            marginBottom: theme.spacing(2),
            maxHeight: 100,
            overflow: "auto",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
        },
        elementContextLabel: {
            fontSize: 11,
            color: theme.palette.text.secondary,
            marginBottom: theme.spacing(0.5),
        },
    }),
);

// Wraps the dialog Paper in react-draggable so the user can reposition it
// out of the way of the element they're trying to style.
function DraggablePaper(props: PaperProps) {
    return (
        <Draggable handle="#guybrush-dialog-title" cancel={'[class*="MuiDialogContent-root"]'}>
            <Paper {...props} />
        </Draggable>
    );
}

interface CssHistoryEntry {
    prompt: string;
    css: string;
    elementContext?: ElementContext;
}

interface GuybrushDialogProps {
    open: boolean;
    onClose: () => void;
    elementContext?: ElementContext;
}

function GuybrushDialog({open, onClose, elementContext}: GuybrushDialogProps) {
    const styles = useStyles();
    const {copilotFunctions} = useFluxServices();

    const [prompt, setPrompt] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | undefined>(undefined);
    const [cssHistory, setCssHistory] = useState<CssHistoryEntry[]>([]);
    const styleElementRef = useRef<HTMLStyleElement | undefined>(undefined);

    const accumulatedCss = useMemo(() => {
        return cssHistory.map((entry) => entry.css).join("\n\n");
    }, [cssHistory]);

    // Lazily creates (or reuses) a single <style> element in <head> where all
    // generated CSS is injected. Using one element means we can replace its
    // textContent on every change to keep the accumulated rules in sync.
    const getOrCreateStyleElement = useCallback((): HTMLStyleElement => {
        if (styleElementRef.current) {
            return styleElementRef.current;
        }
        const existing = document.getElementById(CSS_STYLE_ELEMENT_ID);
        if (existing instanceof HTMLStyleElement) {
            styleElementRef.current = existing;
            return existing;
        }
        const el = document.createElement("style");
        el.id = CSS_STYLE_ELEMENT_ID;
        document.head.appendChild(el);
        styleElementRef.current = el;
        return el;
    }, []);

    const applyAllCss = useCallback(
        (entries: CssHistoryEntry[]) => {
            const styleEl = getOrCreateStyleElement();
            styleEl.textContent = entries.map((e) => e.css).join("\n\n");
        },
        [getOrCreateStyleElement],
    );

    const handleSubmit = useCallback(async () => {
        if (!prompt.trim()) return;

        setIsLoading(true);
        setError(undefined);

        try {
            const result = await copilotFunctions.generateCss({
                prompt: prompt.trim(),
                currentUrl: window.location.href,
                previousChanges: cssHistory.map((entry) => ({
                    prompt: entry.prompt,
                    css: entry.css,
                    elementContext: entry.elementContext
                        ? {
                              tagName: entry.elementContext.tagName,
                              id: entry.elementContext.id,
                              classList: entry.elementContext.classList,
                              parentChain: entry.elementContext.parentChain,
                              outerHtmlSnippet: entry.elementContext.outerHtmlSnippet,
                              nearbyHtmlTree: entry.elementContext.nearbyHtmlTree,
                          }
                        : undefined,
                })),
                elementContext: elementContext
                    ? {
                          tagName: elementContext.tagName,
                          id: elementContext.id,
                          classList: elementContext.classList,
                          parentChain: elementContext.parentChain,
                          outerHtmlSnippet: elementContext.outerHtmlSnippet,
                          nearbyHtmlTree: elementContext.nearbyHtmlTree,
                      }
                    : undefined,
            });

            const cleanCss = result.css
                .replace(/^```css\n?/i, "")
                .replace(/^```\n?/, "")
                .replace(/\n?```$/, "")
                .trim();

            const newEntry: CssHistoryEntry = {prompt: prompt.trim(), css: cleanCss, elementContext};
            setCssHistory((prev) => {
                const updated = [...prev, newEntry];
                applyAllCss(updated);
                return updated;
            });
            setPrompt("");
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setIsLoading(false);
        }
    }, [prompt, cssHistory, copilotFunctions, applyAllCss, elementContext]);

    const handleRemoveEntry = useCallback(
        (index: number) => {
            const newHistory = cssHistory.filter((_, i) => i !== index);
            setCssHistory(newHistory);
            applyAllCss(newHistory);
        },
        [cssHistory, applyAllCss],
    );

    const handleResetAll = useCallback(() => {
        setCssHistory([]);
        const styleEl = document.getElementById(CSS_STYLE_ELEMENT_ID);
        if (styleEl) {
            styleEl.textContent = "";
        }
    }, []);

    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent) => {
            if (event.key === "Enter" && !event.shiftKey && !isLoading) {
                event.preventDefault();
                handleSubmit();
            }
        },
        [handleSubmit, isLoading],
    );

    const buildChangeInstructions = useCallback(() => {
        const cssChanges = cssHistory.map((entry) => `/* Prompt: ${entry.prompt} */\n${entry.css}`).join("\n\n");

        const elementInfo = elementContext
            ? `\nTarget element context:\n${formatElementContextForPrompt(elementContext)}\n`
            : "";

        return `I need you to implement CSS changes permanently in the Flux app codebase (buildwithflux/flux-app).

The following CSS changes were prototyped using Guybrush and need to be made permanent:

\`\`\`css
${cssChanges}
\`\`\`
${elementInfo}
Page URL where changes were tested: ${window.location.href}

Please:
1. Find the appropriate component files and stylesheets where these changes should be applied
2. Use MUI v4 makeStyles/createStyles patterns (not inline styles)
3. Prefer MUI theme values and color objects over raw CSS values where possible
4. Create a PR with the changes`;
    }, [cssHistory, elementContext]);

    // Opens a new Devin session with the accumulated CSS changes and element
    // context baked into the prompt, so Devin can implement them permanently.
    const handleMakeItSoDevin = useCallback(() => {
        const encodedPrompt = encodeURIComponent(buildChangeInstructions());
        const devinUrl = `https://app.devin.ai/?prompt=${encodedPrompt}`;
        window.open(devinUrl, "_blank");
    }, [buildChangeInstructions]);

    // Copies the change instructions to clipboard so the user can paste them
    // into an existing Devin session instead of opening a new one.
    const [copySuccess, setCopySuccess] = useState(false);
    const handleCopyToClipboard = useCallback(() => {
        navigator.clipboard.writeText(buildChangeInstructions()).then(
            () => setCopySuccess(true),
            () => setError("Failed to copy to clipboard"),
        );
    }, [buildChangeInstructions]);

    useEffect(() => {
        if (!copySuccess) return;
        const timer = setTimeout(() => setCopySuccess(false), 2000);
        return () => clearTimeout(timer);
    }, [copySuccess]);

    const handlePromptChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setPrompt(event.target.value);
    }, []);

    const removeEntryCallbacks = useMemo(() => {
        return cssHistory.map((_, index) => () => handleRemoveEntry(index));
    }, [cssHistory, handleRemoveEntry]);

    const dialogClasses = useMemo(() => ({paper: styles.dialogPaper}), [styles.dialogPaper]);

    return (
        <FluxDialog
            open={open}
            onClose={onClose}
            classes={dialogClasses}
            disableEscapeKeyDown={isLoading}
            PaperComponent={DraggablePaper}
            hideBackdrop
        >
            <ClosableDialogTitle id="guybrush-dialog-title" onClose={onClose}>
                Guybrush
            </ClosableDialogTitle>
            <DialogContent>
                {elementContext && (
                    <Box mb={1}>
                        <Typography className={styles.elementContextLabel}>
                            Target: &lt;{elementContext.tagName}&gt;
                            {elementContext.id ? ` #${elementContext.id}` : ""}
                            {elementContext.classList.length > 0
                                ? ` .${elementContext.classList.slice(0, 3).join(".")}`
                                : ""}
                        </Typography>
                        <Box className={styles.elementContextBox}>
                            {elementContext.nearbyHtmlTree || elementContext.parentChain}
                        </Box>
                    </Box>
                )}
                <TextField
                    className={styles.promptInput}
                    fullWidth
                    variant="outlined"
                    size="small"
                    placeholder='Describe CSS changes (e.g., "increase font size", "add 10px margin")'
                    value={prompt}
                    onChange={handlePromptChange}
                    onKeyDown={handleKeyDown}
                    disabled={isLoading}
                    autoFocus
                    multiline
                    maxRows={4}
                />
                <Box display="flex" justifyContent="flex-end" mb={2}>
                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={handleSubmit}
                        disabled={isLoading || !prompt.trim()}
                    >
                        {isLoading ? <CircularProgress size={20} /> : "Apply CSS"}
                    </Button>
                </Box>

                {error && (
                    <Typography color="error" variant="body2" gutterBottom>
                        {error}
                    </Typography>
                )}

                {cssHistory.length > 0 && (
                    <>
                        <Typography variant="subtitle2" gutterBottom>
                            Applied Changes
                        </Typography>
                        {cssHistory.map((entry, index) => (
                            <Box key={`${entry.prompt}-${index}`} className={styles.historyItem}>
                                <Box flex={1} minWidth={0}>
                                    <Typography className={styles.historyPrompt}>{entry.prompt}</Typography>
                                    <Typography className={styles.historyCss}>{entry.css}</Typography>
                                </Box>
                                <IconButton
                                    size="small"
                                    onClick={removeEntryCallbacks[index]}
                                    aria-label="Remove CSS rule"
                                    disabled={isLoading}
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        ))}

                        <Typography variant="subtitle2" gutterBottom>
                            Combined CSS
                        </Typography>
                        <Box className={styles.cssPreview}>{accumulatedCss}</Box>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button size="small" onClick={handleResetAll} disabled={cssHistory.length === 0}>
                    Reset All
                </Button>
                {copySuccess && <Typography className={styles.copySuccess}>Copied!</Typography>}
                <Button
                    className={styles.copyButton}
                    variant="outlined"
                    size="small"
                    onClick={handleCopyToClipboard}
                    disabled={cssHistory.length === 0}
                >
                    Copy Instructions
                </Button>
                <Button
                    className={styles.devinButton}
                    variant="contained"
                    color="secondary"
                    size="small"
                    onClick={handleMakeItSoDevin}
                    disabled={cssHistory.length === 0}
                >
                    Make it so Devin
                </Button>
            </DialogActions>
        </FluxDialog>
    );
}

export default React.memo(GuybrushDialog);
